"use client";

import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { PostReactions, ReactionSummary } from "@theta/api-client";
import { getReactors, removeReaction, setReaction } from "@/lib/api/reactions";
import { REACTION_EMOJI } from "@/lib/reactions";
import { retryUnlessRefused } from "@/lib/hooks/queryRetry";

/**
 * Reacting to a post or a comment.
 *
 * `PUT /theta/reactions/{id}` answered `{counts}` for the post it was given and
 * nothing else, so making the button feel instant took `applyOptimisticSwap`
 * (twenty-two lines moving reactor credit between emoji, clamping at zero,
 * dropping empty rows, trying to reproduce `counts_for_post`), `countsEqual` (a
 * sorted-key comparison, to tell a real change from a re-render) and
 * `pendingCountRef` (a count of in-flight mutations, so a 1 Hz poll landing
 * mid-click could not stomp the guess).
 *
 * All three compensated for one thing: the response did not say what the truth
 * was. `PUT /posts/{id}/reaction` returns the complete `ReactionSummary`, the
 * same document the feed serves. So:
 *
 *  - the arithmetic is gone; nothing here computes a count.
 *  - the pending guard is gone. The authoritative summary is spliced into the
 *    query cache the moment it arrives (`applyReactionToCaches`), so the next
 *    tick already agrees — and that splice also deletes the four
 *    `invalidateQueries` calls the old `useToggleReaction` fired per click,
 *    which cost four refetches of the app's most expensive reads per reaction.
 *  - the optimistic step is one field, `mine`: which emoji is mine flips on
 *    click so the button relabels with no wait, and the counts are the
 *    server's, landing with the response.
 */

/**
 * Splice an authoritative summary into every cached document that carries the
 * reacted-to post or comment.
 *
 * Structural rather than key-by-key: the same post appears in the home feed, a
 * group feed, its own detail page and — as a comment — two comment queries,
 * each a different container shape (infinite pages, a page, a bare document).
 * What they share is the shape of the thing being updated: an object with this
 * `id` and a `reactions` field. Matching on that updates all five without this
 * file knowing how any is nested, and without a sixth place spelling out a
 * query key.
 */
/**
 * Is this value the thing we are replacing — a `ReactionSummary`?
 *
 * `"reactions" in record` is not enough, and the gap is not theoretical: a DM
 * `Message` has an `id` (in a different id space, so a collision with a post id
 * is ordinary) and a `reactions` field that is a `MessageReaction[]`, not a
 * `{counts, mine}` document. Matching on the key overwrote a cached message's
 * array with a summary object, and `MessageBubble` then read
 * `message.reactions.length > 0` as `undefined > 0` — pills gone — and threw
 * `TypeError: … .some is not a function` if the reader clicked that message's
 * react button before the next poll repaired it.
 *
 * Matching on the value's shape keeps the structural walk while making it
 * impossible to write a `ReactionSummary` over anything that was not one.
 */
function isReactionSummary(value: unknown): value is ReactionSummary {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Array.isArray((value as { counts?: unknown }).counts)
  );
}

function spliceReactions(node: unknown, postId: number, reactions: ReactionSummary): unknown {
  if (Array.isArray(node)) {
    let changed = false;
    const next = node.map((item) => {
      const updated = spliceReactions(item, postId, reactions);
      if (updated !== item) changed = true;
      return updated;
    });
    return changed ? next : node;
  }
  if (node === null || typeof node !== "object") return node;

  const record = node as Record<string, unknown>;
  if (record.id === postId && isReactionSummary(record.reactions)) {
    return record.reactions === reactions ? node : { ...record, reactions };
  }

  let changed = false;
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    const updated = spliceReactions(value, postId, reactions);
    if (updated !== value) changed = true;
    next[key] = updated;
  }
  return changed ? next : node;
}

/**
 * `undefined` from the updater means "leave this query alone", and that is not
 * an optimisation. `setQueriesData` visits every cached `["v1", ...]` query, and
 * react-query dispatches a `success` on each one it is handed a value for — even
 * an identical value. That reset `dataUpdatedAt` across the whole cache on every
 * click, restarting every staleTime clock, and cleared the `isInvalidated` flag
 * this function's own caller had just set on the reactor list: react to one post
 * then another, and the first post's reactor list quietly stopped being stale —
 * the original OSO-449 symptom, in an ordinary feed sequence.
 *
 * `spliceReactions` returns the node by reference when it changed nothing, so
 * "did this query contain the post" is one `!==` away.
 */
function applyReactionToCaches(
  queryClient: QueryClient,
  result: PostReactions
): void {
  queryClient.setQueriesData({ queryKey: ["v1"] }, (data: unknown) => {
    const next = spliceReactions(data, result.postId, result.reactions);
    return next === data ? undefined : next;
  });
}

/** The query key a write to this post's reactions drops. */
export function postReactorsKey(postId: number): (string | number)[] {
  return ["v1", "post-reactors", postId];
}

/**
 * Who reacted — the names behind the pills, for the hover tooltip.
 *
 * A separate document from the summary spliced above:
 * `PUT /posts/{id}/reaction` answers the counts, not the reactor list, so there
 * is nothing to splice and the list has to be re-read.
 *
 * OSO-449: `ReactionSummary` fetched this itself, once, into component state
 * behind a `fetchedRef` that was never lowered again — so the viewer's own
 * reaction never appeared in the tooltip. The pills updated (the summary is
 * authoritative and spliced), the names did not, and the emoji just added showed
 * up with nobody under it, or not at all since `buildReactionTooltip` drops an
 * emoji with no names, until a full page reload. Its DM sibling
 * `useMessageReactors` was already a query; this is the same shape, and
 * `usePostReaction` below drops it on every write.
 *
 * Fetched only while `enabled` (on hover): a feed page holds dozens of reacted
 * posts and nobody looks closely at more than one.
 */
export function usePostReactors(postId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: postReactorsKey(postId ?? 0),
    queryFn: () => getReactors(postId as number),
    enabled: enabled && postId !== undefined && postId > 0,
    staleTime: 30 * 1000,
    // Signed out, this is a 401 since OSO-695 — a public event's discussion is
    // where an anonymous reader can hover one.
    retry: retryUnlessRefused,
  });
}

/**
 * Reaction state for one post OR comment — free-form emoji, one per user
 * (picking a new one swaps the previous out). A comment is a post row, so this
 * has always served both.
 *
 * `reactions` is the summary from the document the caller is rendering. It
 * stays the source of truth: the only local state is the emoji a click just
 * chose, held until the server's answer replaces it.
 */
export function usePostReaction(postId: number, reactions: ReactionSummary) {
  const queryClient = useQueryClient();
  /**
   * `undefined` means "no click outstanding"; `{emoji: null}` means "this
   * click removed my reaction". The counts are never overridden — only which
   * one is mine.
   *
   * `url` rides along with the emoji, and has to (OSO-431). The button beside
   * this hook renders the chosen emoji immediately; its picture used to be
   * looked up as `counts.find(c => c.mine)?.url`, and `counts` is still the
   * previous summary for the length of the round trip. Picking a custom emoji
   * therefore drew the emoji you just replaced, or — with nothing to find — fell
   * through to Twemoji with a shortcode, which is a broken image. The picker
   * already hands the url to `onSelect`; this is where it was dropped.
   */
  const [pending, setPending] = useState<
    { emoji: string | null; url: string | null } | undefined
  >(undefined);

  const toggle = useMutation({
    mutationFn: ({ emoji }: { emoji: string | null }) =>
      emoji === null ? removeReaction(postId) : setReaction(postId, emoji),
    onSuccess: (result) => {
      applyReactionToCaches(queryClient, result);
      /**
       * The counts came back with the response and are spliced above; the names
       * did not, and this is the only write that changes them. Without it the
       * tooltip kept whatever it read the first time it was opened, so the
       * viewer's own reaction was missing from it (OSO-449).
       *
       * Removed rather than invalidated, and the difference is visible: an
       * invalidated query still holds its old rows, so the next hover renders
       * them while the refetch is in flight — and `buildReactionTooltip` drops
       * an emoji no name is under, so the pill just added blinks in a moment
       * after the tooltip opens. With nothing cached the tooltip simply waits.
       */
      queryClient.removeQueries({ queryKey: postReactorsKey(result.postId) });
    },
    // The write failed, so the document never changed and it is authoritative
    // again. This is the only path that drops the override by hand.
    onError: () => setPending(undefined),
  });

  /**
   * The override ends when the document agrees with it, not when the request
   * settles — which is what the deleted `pendingCountRef` was compensating for.
   * With two rapid clicks the first response would clear the guard while the
   * second was in flight and the card flashed the first click's answer. Here the
   * first response splices a summary whose `mine` is the first emoji, not what
   * this click chose, so the override stays; the second splices the second
   * emoji, they agree, and the override lifts with nothing to flicker between.
   * Adjusting state during render is React's own escape hatch for this and lands
   * in the same commit rather than one render late.
   */
  if (pending !== undefined && reactions.mine === pending.emoji) {
    setPending(undefined);
  }

  const currentReaction = pending === undefined ? reactions.mine : pending.emoji;
  const counts = reactions.counts;
  /**
   * The image for MY reaction — the authoritative summary's once it agrees,
   * the clicked one while a click is outstanding. `null` for a standard emoji,
   * which has no image at all.
   */
  const currentReactionUrl =
    pending === undefined ? (counts.find((c) => c.mine)?.url ?? null) : pending.url;

  /** `url` is the picker's, for a custom emoji; standard emoji pass nothing. */
  const handleReact = (emoji: string, url?: string | null) => {
    const next = currentReaction === emoji ? null : emoji;
    setPending({ emoji: next, url: next === null ? null : (url ?? null) });
    toggle.mutate({ emoji: next });
  };

  const handleQuickLike = () => handleReact(REACTION_EMOJI.like);

  return { currentReaction, currentReactionUrl, counts, handleReact, handleQuickLike };
}
